/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { createObservableDataAction } from '@msdyn365-commerce/action-internal';
import msdyn365Commerce, {
    getFriendlyName,
    getParameterizeFeatureFlagsWithRequestContext,
    IAction,
    IActionContext,
    IParameterizeFeatureFlags,
    parameterize,
    checkVersionForFeature,
    getCatalogIdForSdk
} from '@msdyn365-commerce/core-internal';
import { getCategoriesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CategoriesDataActions.g';
import { getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { Category, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { CategoryLinkInput, IPageTypeContext, ProductLinkInput } from '../hydrator-action-inputs';

type CategoryWithNeutralizedName = Category & { NeutralizedName?: string };
/**
 * Computes a root category's SLUG
 *
 * @param rootCategory The category for which you want to determine the slug
 * @param ctx Action context
 */
export const getCategorySlug = (
    rootCategory: CategoryWithNeutralizedName,
    categories: CategoryWithNeutralizedName[],
    parameterizeFeatureFlags?: IParameterizeFeatureFlags,
    context?: IActionContext
): string => {
    let slug: string = parameterize(rootCategory.Name, undefined, parameterizeFeatureFlags, rootCategory.NeutralizedName);
    let currentCategory: Category = rootCategory;
    while (currentCategory.ParentCategory) {
        const parentId = currentCategory.ParentCategory;
        const parentCategory: CategoryWithNeutralizedName | undefined = categories.find((category: Category) => {
            if (category.RecordId) {
                return category.RecordId === parentId;
            }
            return false;
        });
        if (!parentCategory) {
            break;
        }

        let parentCategoryWithLocalizedName = parentCategory;
        if (context && isUrlLocalizationEnabled(parameterizeFeatureFlags)) {
            const localName = getFriendlyName(context.requestContext.locale, parentCategory.NameTranslations);
            parentCategoryWithLocalizedName = <CategoryWithNeutralizedName>{ ...parentCategory };
            parentCategoryWithLocalizedName.NeutralizedName = parentCategory.Name;
            parentCategoryWithLocalizedName.Name = localName || parentCategoryWithLocalizedName.NeutralizedName;
        }

        slug = `${parameterize(
            parentCategoryWithLocalizedName.Name,
            undefined,
            parameterizeFeatureFlags,
            parentCategoryWithLocalizedName.NeutralizedName
        )}/${slug}`;
        currentCategory = parentCategoryWithLocalizedName;
    }
    return slug.length > 0 ? `/${slug}` : '';
};

const isUrlLocalizationEnabled = (parameterizeFeatureFlags?: IParameterizeFeatureFlags) => {
    return (
        (msdyn365Commerce.platformSettings && msdyn365Commerce.platformSettings.enableUrlLocalization) ||
        (parameterizeFeatureFlags && parameterizeFeatureFlags.enableUrlLocalization)
    );
};

/**
 * Returns an object containing information used to by the URL builder to build
 * the formatted product or category URL
 * =
 * @param input Action input
 * @param context Action context
 */
const getPageTypeContextById = async (input: ProductLinkInput | CategoryLinkInput, context: IActionContext): Promise<IPageTypeContext> => {
    // If product ID exits get the product details for that product
    const pageTypeContext: IPageTypeContext = {};
    if (input instanceof ProductLinkInput && input.productId) {
        try {
            const product: SimpleProduct = await getByIdAsync({ callerContext: context }, input.productId, input.channelId);
            if (!(product && product.Name)) {
                throw new Error(`Unable to retrieve product details for product id: ${input.productId}`);
            }
            pageTypeContext.product = { Name: product.Name || '', RecordId: product.RecordId };
        } catch (err) {
            context.telemetry.error(err);
            throw new Error(`Unable run getById action. Error: ${err}`);
        }
    }
    const catalogFeature = checkVersionForFeature('catalogFeature');
    const catalogid = (catalogFeature && getCatalogIdForSdk(context.requestContext, 0)) || undefined;

    // Get the category details for the passed category id
    // @ts-ignore old retail proxy contract does not have catalogid.
    return getCategoriesAsync({ callerContext: context }, input.channelId, catalogid)
        .then((categories: Category[]) => {
            // Find the category by id from the list of returned categories
            const rootCategory: Category | undefined = categories.find((category: Category) => {
                if (category.RecordId) {
                    return category.RecordId === input.categoryId;
                }
                return false;
            });
            if (!rootCategory) {
                throw new Error(`Unable to retrieve category details for category id: ${input.categoryId}`);
            }
            const localName = getFriendlyName(context.requestContext.locale, rootCategory.NameTranslations);
            const rootCategoryWithNeutralizedName = <CategoryWithNeutralizedName>{ ...rootCategory };
            rootCategoryWithNeutralizedName.NeutralizedName = rootCategory.Name;
            rootCategoryWithNeutralizedName.Name = localName || rootCategoryWithNeutralizedName.NeutralizedName;
            // Compute the found category's slug
            const parameterizeFeatureFlags: IParameterizeFeatureFlags = getParameterizeFeatureFlagsWithRequestContext(
                context.requestContext
            );
            const slug = getCategorySlug(rootCategoryWithNeutralizedName, categories, parameterizeFeatureFlags, context);
            pageTypeContext.category = { Slug: slug, RecordId: rootCategoryWithNeutralizedName.RecordId };
            return Promise.resolve(pageTypeContext);
        })
        .catch(err => {
            // In the event of an error return just the category id
            context.telemetry.error(err);
            throw new Error(`Unable to run getCategories action. Error: ${err}`);
        });
};

export const getPageTypeContextByIdAction = createObservableDataAction({
    action: <IAction<IPageTypeContext>>getPageTypeContextById
});
